import React, { useEffect, useState, useRef } from 'react'
import { Button, Form, Input, Modal, Tooltip, Upload, message } from 'antd'
import {
  DeleteOutlined,
  FileTextOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { inject, observer } from 'mobx-react'
import { toJS } from 'mobx'

import utils from '../../../utils'
import validator from '../../../validator'
import RichEditor from '../../../components/RichEditor/RichEditor'
import {
  UploadFileListItem,
  UploadFileListWrapper,
  FormActionFooter,
} from './BoardReportCreateModalStyled'
import PopupRenameFile from '../../../components/PopupRenameFile'

const BoardReportCreateModal = props => {
  const {
    boardReportStore,
    fileStore,
    loadingAnimationStore,
    connectedDocumentStore,
    onCancel,
    onSuccess,
    visible,
  } = props

  const [form] = Form.useForm()
  const EDITOR_REF = useRef()
  const [editorContent, setEditorContent] = useState('')
  const [fileList, setFileList] = useState([])
  const [fileFromTruc, setFileFromTruc] = useState([])
  const [isRenameFileFromTruc, setIsRenameFileFromTruc] = useState(false)
  const [fileTargetRename, setFileTargetRename] = useState(null)
  const [fileExt, setFileExt] = useState('')
  const [isModalVisibleRenameFile, setIsModalVisibleRenameFile] = useState(
    false
  )
  const [fieldsRenameFile, setFieldsRenameFile] = useState([
    { name: ['rename'], value: '' },
  ])

  const handleEditorChange = content => {
    setEditorContent(content)
    form.setFieldsValue({ content: content })
  }

  const handleChangeFile = info => {
    const incomingFiles = info.fileList
    const uniqueFiles = []
    const seenNames = new Set()
    const duplicatedFiles = new Set()

    for (const file of incomingFiles) {
      if (seenNames.has(file.name)) {
        duplicatedFiles.add(file.name)
      } else {
        seenNames.add(file.name)
        uniqueFiles.push(file)
      }
    }

    if (duplicatedFiles.size > 0) {
      message.warning('File đã có trong danh sách')
    }

    setFileList(uniqueFiles)
  }

  const handleRemoveFileFromUploadList = fileUID => {
    const newFileList = fileList.filter(file => file.uid !== fileUID)
    setFileList(newFileList)
    if (newFileList.length === 0) {
      form.setFieldsValue({ attachments: [] })
    }
  }

  const handleRemoveFileFromTruc = file_id => {
    setFileFromTruc(
      toJS([...fileFromTruc]).filter(file => file.fileId !== file_id)
    )
  }

  const handleOpenPopupRenameFile = (file, index) => {
    setFieldsRenameFile([
      { name: ['rename'], value: utils.removeExtensionFile(file.name) },
    ])
    setFileExt('.' + utils.getExtensionFile(file.name))
    setFileTargetRename({ ...file, index })
    setIsModalVisibleRenameFile(true)
  }

  const renameFile = (originalFile, newName) => {
    return new File([originalFile], newName, {
      type: originalFile.type,
      lastModified: originalFile.lastModified,
    })
  }

  const submitRenameFile = async value => {
    const index = fileTargetRename?.index
    const trimmedName = value.rename.trim()

    if (!trimmedName) {
      message.warning('Tên file không được để trống')
      return
    }

    const newFileName = trimmedName + fileExt
    const normalizedNewName = newFileName.trim().toLowerCase()

    const currentFileNames = [
      ...fileList.map((f, i) =>
        i !== index || isRenameFileFromTruc ? f.name : null
      ),
      ...fileFromTruc.map((f, i) =>
        i !== index || !isRenameFileFromTruc ? f.name : null
      ),
    ].filter(Boolean)

    const normalizedCurrentNames = currentFileNames.map(name =>
      name.trim().toLowerCase()
    )

    if (normalizedCurrentNames.includes(normalizedNewName)) {
      message.warning('Tên file đã tồn tại, vui lòng chọn tên khác')
      return
    }

    if (isRenameFileFromTruc) {
      const newList = [...fileFromTruc]
      newList[index] = {
        fileId: fileTargetRename.fileId,
        name: newFileName,
      }
      setFileFromTruc(newList)
    } else {
      const newList = [...fileList]
      if (fileTargetRename.originFileObj) {
        newList[index].name = newFileName
        newList[index].originFileObj = renameFile(
          newList[index].originFileObj,
          newFileName
        )
      } else {
        await fileStore.handleRenameFile(fileTargetRename.id, newFileName)
        newList[index].name = newFileName
      }
      setFileList(newList)
    }

    setIsModalVisibleRenameFile(false)
    message.success('Đổi tên file thành công')
  }

  const handleCloseModal = () => {
    form.resetFields()
    setEditorContent('')
    setFileList([])
    setFileFromTruc([])
    onCancel()
  }

  const onFinish = async values => {
    const html = EDITOR_REF.current?.editor.getData() || ''

    loadingAnimationStore.showSpinner(true)
    try {
      const attachments = []
      const signedIds = fileList.filter(f => f.isSigned).map(f => f.id)
      attachments.push(...signedIds)

      const unsignedFiles = fileList.filter(f => !f.isSigned)
      const uploaded = await Promise.all(
        unsignedFiles.map(file => {
          const fd = new FormData()
          fd.append('file', file.originFileObj, file.name)
          return fileStore.uploadFile(fd)
        })
      )
      attachments.push(...uploaded.map(r => r.data.file_id))

      const downloaded = await Promise.all(
        fileFromTruc.map(f => fileStore.downloadAttachmentTruc(f.fileId))
      )
      const reUploaded = await Promise.all(
        downloaded.map((res, idx) => {
          const fd = new FormData()
          fd.append('file', res.data, fileFromTruc[idx].name)
          return fileStore.uploadFile(fd)
        })
      )
      attachments.push(...reUploaded.map(r => r.data.file_id))

      const payload = {
        title: values.title,
        content: html,
        attachments,
      }
      await boardReportStore.createBoardReport(payload)

      message.success('Tạo mới báo cáo thành công')
      form.resetFields()
      setEditorContent('')
      setEditorContent('')
      setFileList([])
      setFileFromTruc([])
      onCancel()
      onSuccess()
    } catch (err) {
      message.error(err?.vi || 'Đã có lỗi xảy ra!')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }

  useEffect(() => {
    const dataReceive = connectedDocumentStore.dataReceiveDocument
    if (dataReceive?.attachments?.length) {
      setFileFromTruc([...toJS(dataReceive.attachments)])
    }
  }, [])

  return (
    <Modal
      visible={visible}
      onCancel={handleCloseModal}
      footer={null}
      destroyOnClose
      width={700}
      title="Tạo mới báo cáo">
      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        onFinish={onFinish}
        style={{ paddingTop: 10 }}>
        <Form.Item
          name="title"
          label={<span>Tiêu đề</span>}
          rules={[
            { required: true, message: 'Vui lòng nhập tiêu đề' },
            { validator: validator.validateInputString },
          ]}>
          <Input maxLength={200} placeholder="Nhập tiêu đề báo cáo" />
        </Form.Item>

        <Form.Item label="Nội dung" name="content">
          <RichEditor
            EDITOR_REF={EDITOR_REF}
            placeholder="Nhập nội dung báo cáo"
            onEditorChange={handleEditorChange}
            editorContent={editorContent}
          />
        </Form.Item>

        <Form.Item name="attachments" label={<span>Tài liệu đính kèm</span>}>
          <Upload
            fileList={fileList}
            multiple
            beforeUpload={() => false}
            onChange={handleChangeFile}
            showUploadList={false}>
            <Button icon={<UploadOutlined />}>Chọn tài liệu đính kèm</Button>
          </Upload>
        </Form.Item>

        <UploadFileListWrapper>
          {fileList.map((file, index) => (
            <UploadFileListItem key={file.uid || file.id}>
              <FileTextOutlined />
              <span>{file.name}</span>
              <Button
                className="rename"
                type="text"
                onClick={() => {
                  setIsRenameFileFromTruc(false)
                  handleOpenPopupRenameFile(file, index)
                }}>
                Đổi tên
              </Button>
              <Tooltip title="Xoá tập tin">
                <DeleteOutlined
                  onClick={() => handleRemoveFileFromUploadList(file.uid)}
                />
              </Tooltip>
            </UploadFileListItem>
          ))}

          {fileFromTruc.map((file, index) => (
            <UploadFileListItem key={file.fileId}>
              <FileTextOutlined style={{ color: '#1890FF' }} />
              <span style={{ color: '#1890FF' }}>{file.name}</span>
              <Button
                className="rename"
                type="text"
                onClick={() => {
                  setIsRenameFileFromTruc(true)
                  handleOpenPopupRenameFile(file, index)
                }}>
                Đổi tên
              </Button>
              <Tooltip title="Xoá tập tin">
                <DeleteOutlined
                  onClick={() => handleRemoveFileFromTruc(file.fileId)}
                />
              </Tooltip>
            </UploadFileListItem>
          ))}
        </UploadFileListWrapper>

        <FormActionFooter>
          <Button onClick={handleCloseModal}>Huỷ bỏ</Button>
          <Button type="primary" htmlType="submit" style={{ marginLeft: 10 }}>
            Tạo mới
          </Button>
        </FormActionFooter>
      </Form>

      <PopupRenameFile
        isModalVisibleRenameFile={isModalVisibleRenameFile}
        handleCloseModalRenameFile={() => setIsModalVisibleRenameFile(false)}
        submitRenameFile={submitRenameFile}
        fieldsRenameFile={fieldsRenameFile}
        fileExt={fileExt}
      />
    </Modal>
  )
}

export default inject(
  'boardReportStore',
  'fileStore',
  'authorityStore',
  'loadingAnimationStore',
  'bookStore',
  'selectPeopleStore',
  'connectedDocumentStore'
)(observer(BoardReportCreateModal))
